<script setup>
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import { api } from '@/api/client.js';
import AnalyticsChart from '@/components/charts/AnalyticsChart.vue';
import Tabs from '@/components/ds/Tabs.vue';
import BarRankRow from '@/components/panels/BarRankRow.vue';
import PanelCard from '@/components/panels/PanelCard.vue';
import { usePanelState } from '@/composables/usePanelState.js';
import { usePoll } from '@/composables/usePoll.js';
import { useTenantStore } from '@/stores/tenant.js';
import { TECH_EMPTY } from '@/utils/emptyStateCopy.js';

// Distribución browser/OS/device por sesiones. El parsing de UA es
// aproximado por diseño (regex server-side sin deps) — el hint del
// footer lo etiqueta siempre.

const tenant = useTenantStore();
const { activeAppId, timeWindow } = storeToRefs(tenant);
const ps = usePanelState('loading');
const data = ref({ totalSessions: 0, browsers: [], os: [], devices: [], truncated: false });
const activeTab = ref('browsers');

const TABS = [
  { value: 'browsers', label: 'Browsers' },
  { value: 'os', label: 'OS' },
];

async function fetchFn({ signal }) {
  if (!activeAppId.value) return;
  return api.events.tech({ appId: activeAppId.value, window: timeWindow.value, signal });
}

const { tick, status: refreshStatus, lastUpdatedAt } = usePoll(fetchFn, {
  watch: [() => tenant.changeKey],
  onSuccess(result) {
    if (!result || result.ok !== true) return;
    data.value = {
      totalSessions: result.totalSessions ?? 0,
      browsers: result.browsers ?? [],
      os: result.os ?? [],
      devices: result.devices ?? [],
      truncated: result.truncated === true,
    };
    ps.setState(data.value.browsers.length === 0 ? 'empty' : 'data');
  },
  onError(err) {
    ps.setState('error', {
      message: "Couldn't load technology data",
      detail: err?.message ?? '',
    });
  },
});

ps.setRetryHandler(() => tick());

// Exponer ps para los panelRefs del LandingView (error global / retry all).
defineExpose({ ps, refreshStatus });

function withPct(rows) {
  const total = data.value.totalSessions;
  return rows.map((row) => ({
    ...row,
    pct: total > 0 ? (row.count / total) * 100 : null,
  }));
}

const activeRows = computed(() =>
  withPct(activeTab.value === 'os' ? data.value.os : data.value.browsers));

// Barra segmentada de devices (desktop/mobile/tablet/unknown) con
// colores de la escala --chart-N y leyenda.
const DEVICE_COLORS = Object.freeze({
  desktop: 'var(--chart-1)',
  mobile: 'var(--chart-2)',
  tablet: 'var(--chart-3)',
  unknown: 'var(--fg-tertiary)',
});

const deviceSegments = computed(() => {
  const total = data.value.devices.reduce((acc, d) => acc + d.count, 0);
  if (total === 0) return [];
  return data.value.devices.map((d) => ({
    name: d.name,
    count: d.count,
    pct: (d.count / total) * 100,
    color: DEVICE_COLORS[d.name] ?? 'var(--fg-tertiary)',
  }));
});

const deviceAria = computed(() =>
  `Device split: ${deviceSegments.value.map((s) => `${s.name} ${Math.round(s.pct)}%`).join(', ')}`);

const hint = computed(() => data.value.truncated
  ? 'Approximate, parsed from user agent. Long tail truncated.'
  : 'Approximate, parsed from user agent.');
</script>

<template>
  <PanelCard
    title="Technology"
    help-key="technology"
    :state="ps.state.value"
    :empty-headline="TECH_EMPTY.headline"
    :empty-body="TECH_EMPTY.body"
    :error-message="ps.errorMessage.value"
    :error-detail="ps.errorDetail.value"
    :on-retry="ps.retry"
    :refresh-status="refreshStatus"
    :last-updated-at="lastUpdatedAt"
  >
    <div class="tech">
      <p class="tech__summary">
        <strong>{{ data.totalSessions.toLocaleString() }}</strong> sessions profiled
      </p>
      <div
        v-if="deviceSegments.length"
        class="tech__devices"
      >
        <AnalyticsChart
          type="doughnut"
          :labels="deviceSegments.map((row) => row.name)"
          :datasets="[{ label: 'Sessions', data: deviceSegments.map((row) => row.count), backgroundColor: deviceSegments.map((row) => row.color) }]"
          :aria-label="deviceAria"
          percentage
          :percentage-total="data.totalSessions"
          :center-value="String(deviceSegments.length)"
          center-label="device types"
        />
        <div class="tech__devices-legend">
          <span
            v-for="seg in deviceSegments"
            :key="`l-${seg.name}`"
            class="tech__devices-item"
          >
            <span
              class="tech__devices-swatch"
              :style="{ background: seg.color }"
              aria-hidden="true"
            />
            {{ seg.name }} {{ Math.round(seg.pct) }}%
            <span class="tech__device-count">{{ seg.count.toLocaleString() }} sessions</span>
          </span>
        </div>
      </div>
      <Tabs
        v-model="activeTab"
        :items="TABS"
        size="sm"
      />
      <div class="tech__rows">
        <p class="tech__hint">
          Sessions · share of all profiled sessions
        </p>
        <BarRankRow
          v-for="row in activeRows"
          :key="`${activeTab}-${row.name}`"
          :label="row.name"
          :count="row.count"
          :pct="row.pct"
        />
        <p
          v-if="!activeRows.length"
          class="tech__hint"
        >
          No data for this dimension.
        </p>
      </div>
      <p class="tech__hint">
        {{ hint }}
      </p>
    </div>
    <template
      v-if="data.browsers.length"
      #tableFallback
    >
      <table class="tech-table">
        <caption class="visually-hidden">
          Technology split by sessions — browsers, OS and devices
        </caption>
        <thead>
          <tr>
            <th scope="col">
              Dimension
            </th>
            <th scope="col">
              Value
            </th>
            <th
              scope="col"
              class="numeric"
            >
              Sessions
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in data.browsers"
            :key="`tb-${row.name}`"
          >
            <td>Browser</td>
            <td>{{ row.name }}</td>
            <td class="numeric">
              {{ row.count }}
            </td>
          </tr>
          <tr
            v-for="row in data.os"
            :key="`to-${row.name}`"
          >
            <td>OS</td>
            <td>{{ row.name }}</td>
            <td class="numeric">
              {{ row.count }}
            </td>
          </tr>
          <tr
            v-for="row in data.devices"
            :key="`td-${row.name}`"
          >
            <td>Device</td>
            <td>{{ row.name }}</td>
            <td class="numeric">
              {{ row.count }}
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </PanelCard>
</template>

<style scoped>
.tech {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.tech__devices {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-3);
  border-radius: var(--radius-xl);
  background: var(--bg-subtle);
}

.tech__devices-legend {
  display: grid;
  gap: var(--space-3);
  font-size: var(--text-xs);
  color: var(--fg-secondary);
}

.tech__devices-item {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-1);
}

.tech__devices-swatch {
  width: 0.6rem;
  height: 0.6rem;
  border-radius: var(--radius-full);
  display: inline-block;
}

.tech__rows {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.tech__device-count {
  flex-basis: 100%;
  color: var(--fg-secondary);
}

.tech__summary {
  margin: 0;
  color: var(--fg-secondary);
  font-size: var(--text-sm);
}

.tech__summary strong {
  font-size: var(--text-2xl);
  color: var(--fg-primary);
  font-variant-numeric: tabular-nums;
}

.tech__hint {
  font-size: var(--text-xs);
  color: var(--fg-tertiary);
  margin: 0;
}

.tech-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-xs);
}

.tech-table th,
.tech-table td {
  padding: var(--space-1) var(--space-2);
  text-align: left;
  border-bottom: var(--bw-thin) solid var(--border-default);
}

.tech-table th {
  color: var(--fg-secondary);
  font-weight: var(--fw-medium);
}

.tech-table .numeric {
  text-align: right;
  font-feature-settings: "tnum";
}
</style>
